/**
 * PortfolioPublishButton — bascule l'œuvre entre `draft`, `published` et `archived`.
 *
 * Politique éditoriale (cf. mémoire `project_publication_editorial_policy`) :
 * une œuvre n'est mise en ligne portfolio que si Stéphanie a écrit
 * substantiellement (description ≥ 100 chars, story ≥ 80 chars). Le verrou
 * réel vit côté backend (`publication_service.publish_work` lève 422 avec
 * la liste des manques) ; ce composant duplique l'évaluation localement
 * pour donner du feedback instantané sans appeler l'API.
 *
 * Trois états visibles :
 *  - `work.status === 'draft'` : bouton primary "Publier sur sgraphic.art".
 *    Disabled tant que les seuils FR ne passent pas, avec liste compacte
 *    des manques sous le bouton.
 *  - `work.status === 'published'` : bouton ghost "Retirer de sgraphic.art".
 *  - `work.status === 'archived'` : bouton ghost "Sortir de l'archive"
 *    (repasse en `draft`, l'historique de publication est conservé).
 */

import { toast } from 'sonner'

import Button from '@/components/ui/Button'
import {
  usePublishWorkToPortfolio,
  useUnarchiveWork,
  useUnpublishWorkFromPortfolio,
} from '@/hooks/useWorks'
import { evaluatePortfolioPublishability, type PortfolioMissingField } from '@/schemas/works'
import type { WorkResponse } from '@/types/works'

import './PortfolioPublishButton.css'

interface PortfolioPublishButtonProps {
  work: WorkResponse
}

const FIELD_LABELS: Record<PortfolioMissingField['field'], string> = {
  description_fr: 'description',
  story_fr: 'story',
}

export default function PortfolioPublishButton({ work }: PortfolioPublishButtonProps) {
  const publish = usePublishWorkToPortfolio(work.id)
  const unpublish = useUnpublishWorkFromPortfolio(work.id)
  const unarchive = useUnarchiveWork(work.id)

  if (work.status === 'archived') {
    const handleUnarchive = async () => {
      try {
        await unarchive.mutateAsync()
        toast.success('Œuvre sortie de l’archive (brouillon).')
      } catch (err) {
        const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
        toast.error(`Désarchivage impossible : ${detail}`)
      }
    }
    return (
      <Button variant="ghost" size="sm" loading={unarchive.isPending} onClick={handleUnarchive}>
        Sortir de l’archive
      </Button>
    )
  }

  if (work.status === 'published') {
    const handleUnpublish = async () => {
      try {
        await unpublish.mutateAsync()
        toast.success('Œuvre retirée de sgraphic.art.')
      } catch (err) {
        const detail = (err as { detail?: string })?.detail ?? 'Erreur inconnue'
        toast.error(`Retrait impossible : ${detail}`)
      }
    }
    return (
      <Button variant="ghost" size="sm" loading={unpublish.isPending} onClick={handleUnpublish}>
        Retirer de sgraphic.art
      </Button>
    )
  }

  const check = evaluatePortfolioPublishability(work)
  const handlePublish = async () => {
    try {
      await publish.mutateAsync()
      toast.success('Œuvre publiée sur sgraphic.art.')
    } catch (err) {
      // 422 = seuils non atteints (rare si le bouton est correctement
      // disabled, mais possible en course autosave/click). Le `detail` du
      // backend est l'objet { message, missing[] } — on retombe sur le
      // message générique pour rester simple.
      const detail = (err as { detail?: { message?: string } | string })?.detail
      const msg = typeof detail === 'string' ? detail : (detail?.message ?? 'Erreur inconnue')
      toast.error(`Mise en ligne impossible : ${msg}`)
    }
  }

  return (
    <div className="portfolio-publish-button-wrap">
      <Button
        variant="primary"
        size="sm"
        loading={publish.isPending}
        disabled={!check.ready}
        onClick={handlePublish}
      >
        Publier sur sgraphic.art
      </Button>
      {!check.ready && (
        <p className="portfolio-publish-button-missing">
          Pour mettre en ligne, il manque :{' '}
          {check.missing
            .map((m) => `${FIELD_LABELS[m.field]} (${m.currentChars} / ${m.minChars} caractères)`)
            .join(', ')}
          .
        </p>
      )}
    </div>
  )
}
